import { sortView, aggregate } from './query.js';
import { displayDate } from './format.js';
import { setHtml } from './css.js';
import { groupNodes, flattenServerGroups } from './server.js';
import { EXTRA, MAX_SCROLL_PX, CHOSUNG, esc, colAggs } from './grid-util.js';
export class GridGroup {
    // ── 트리 ──
    expandAll() { this.treeCollapsed.clear(); this.rebuild(); }
    collapseAll() { const t = this.opts.tree; if (!t)
        return; this.treeCollapsed = new Set([...this.treeKids].map(r => String(this.ds.get(r, t.idCol)))); this.rebuild(); }
    toggleNode(row, open) {
        const t = this.opts.tree;
        if (!t || !this.treeKids.has(row))
            return;
        const id = String(this.ds.get(row, t.idCol));
        const isOpen = !this.treeCollapsed.has(id);
        if (open === isOpen)
            return;
        isOpen ? this.treeCollapsed.add(id) : this.treeCollapsed.delete(id);
        if (!isOpen && t.loadChildren && !this.treeAllKids.has(row) && !this.treeLoaded.has(id)) {
            this.loadTreeChildren(row, id);
            return;
        }
        this.rebuild();
    }
    /** @internal */ async loadTreeChildren(row, id) {
        const t = this.opts.tree;
        if (this.treeLoading.has(id))
            return;
        this.treeLoading.add(id);
        this.bump();
        try {
            const rows = await t.loadChildren(row, this);
            this.treeLoaded.add(id);
            const add = rows.map(r => (r[t.parentCol] == null || r[t.parentCol] === '' ? { ...r, [t.parentCol]: this.ds.get(row, t.idCol) } : r));
            this.treeLoading.delete(id);
            const res = this.ds.applyTransaction({ add }); // 'merge' → 트리 다시 구성 (체크·접힘 유지)
            if (t.checkCascade && this.checked.has(row)) {
                for (const r of res.added)
                    this.checked.add(r);
                this.rebuild();
            }
            if (!res.added.length)
                this.rebuild();
        }
        catch (e) {
            this.treeLoading.delete(id);
            this.treeCollapsed.add(id);
            this.rebuild();
            this.setError(`하위 항목을 불러오지 못했습니다: ${e?.message ?? e}`, () => this.toggleNode(row, true));
        }
    }
    /** 노드가 지연 로딩 중인지 */
    isTreeLoading(row) { const t = this.opts.tree; return !!t && this.treeLoading.has(String(this.ds.get(row, t.idCol))); }
    /** 체크 상태: true / false / 'mixed'(하위 일부만) */
    getCheckState(row) { return this.checked.has(row) ? true : this.treeMixed.has(row) ? 'mixed' : false; }
    /** @internal */ recalcMixed(kids = this.treeAllKids) {
        this.treeMixed.clear();
        if (!this.checked.size)
            return;
        const parent = new Map();
        for (const [p, rs] of kids)
            if (p >= 0)
                for (const r of rs)
                    parent.set(r, p);
        for (const r of this.checked) {
            let p = parent.get(r), g = 0;
            while (p != null && g++ < 64) {
                if (!this.checked.has(p))
                    this.treeMixed.add(p);
                p = parent.get(p);
            }
        }
    }
    /** 체크 연동 (트리): 자신+하위 전부를 on 으로, 그다음 조상은 자식이 모두 체크면 체크·아니면 해제 */
    /** @internal */ cascadeCheck(row, on) {
        const kids = this.treeAllKids;
        const stack = [row], seen = new Set();
        while (stack.length) {
            const r = stack.pop();
            if (seen.has(r))
                continue;
            seen.add(r);
            on ? this.checked.add(r) : this.checked.delete(r);
            for (const k of kids.get(r) ?? [])
                stack.push(k);
        }
        const parent = new Map();
        for (const [p, rs] of kids)
            if (p >= 0)
                for (const r of rs)
                    parent.set(r, p);
        let p = parent.get(row), g = 0;
        while (p != null && g++ < 64) {
            const all = (kids.get(p) ?? []).every(k => this.checked.has(k));
            all ? this.checked.add(p) : this.checked.delete(p);
            p = parent.get(p);
        }
        this.recalcMixed();
    }
    getTreeLevel(row) { return this.treeLevel.get(row) ?? 0; }
    /** 그룹 지정. 배열이면 다단계 */
    setGroupBy(cols) { this.opts.groupBy = cols; this.groupToggled.clear(); this.groupCollapseAll = false; this.srvExpandAll = false; this.afterGroupChange(); }
    getGroupBy() { return this.groupCols(); }
    addGroup(col, at) { const g = this.groupCols().filter(c => c !== col); g.splice(at ?? g.length, 0, col); this.setGroupBy(g); }
    removeGroup(col) { this.setGroupBy(this.groupCols().filter(c => c !== col)); }
    setGroupDir(col, dir) { this.groupDir.set(col, dir); this.afterGroupChange(); }
    expandAllGroups() { this.groupCollapseAll = false; this.srvExpandAll = true; this.groupToggled.clear(); this.rebuild(); }
    collapseAllGroups() { this.groupCollapseAll = true; this.srvExpandAll = false; this.groupToggled.clear(); this.rebuild(); }
    /** @internal */ afterGroupChange() { if (this.opts.hideGroupedColumns)
        this.layout(); this.renderGroupPanel(); this.bodyPanes[1].scrollTop = 0; this.focus = { vi: 0, ci: this.focus.ci }; this.rebuild(); this.notifyLayout(); }
    /** @internal */ groupCols() {
        // 서버 모드: 서버 GROUP BY (머리행 건수·집계, 펼칠 때 멤버) — 서버 원본 컬럼만
        if (this.srm)
            return this.groupColsRaw().filter(c => !this.colInfo(c)?.def.value);
        return this.groupColsRaw();
    }
    /** @internal */ groupColsRaw() { const g = this.opts.groupBy; return (g == null ? [] : Array.isArray(g) ? g : [g]).filter(c => this.ds?.cols[c]); }
    /** @internal */ isGrouped() { return this.groupCols().length > 0; }
    /** 서버에 요청할 집계 ("col:fn"): 합계 줄·그룹 소계에 쓰는 컬럼 집계 (서버 컬럼만) */
    /** @internal */ srvAggSpecs() {
        const out = [];
        for (const c of this.cols) {
            if (c.special || c.def.value || !this.ds.cols[c.id])
                continue;
            const fns = [...colAggs(c.def), ...(typeof c.def.summary === 'string' ? [c.def.summary] : [])];
            for (const f of fns)
                if (!out.includes(c.id + ':' + f))
                    out.push(c.id + ':' + f);
        }
        return out;
    }
    /** @internal */ fetchServerTotals(q) {
        const aggs = this.opts.showFooter ? this.srvAggSpecs() : [];
        const sig = aggs.length ? JSON.stringify([q.filter, q.quick, aggs]) : '';
        if (sig === this.srvTotals.sig)
            return;
        const st = this.srvTotals = { sig, aggs: null };
        if (!sig)
            return;
        this.srm.groups(null, q, [], aggs).then(r => { if (this.srvTotals !== st)
            return; st.aggs = r[0]?.aggs ?? {}; this.renderFooter(); }).catch(() => { });
    }
    /** @internal */ rebuildServerGroups(q, gcols) {
        const aggs = this.srvAggSpecs();
        const sig = JSON.stringify([q.filter, q.quick, q.sort, gcols, gcols.map(c => this.groupDir.get(c) ?? 'asc'), aggs]);
        let st = this.srvG;
        if (!st || st.sig !== sig) {
            st = this.srvG = { sig, roots: null, q, gcols, aggs };
            this.srm.suspend();
            this.bodyPanes[1].scrollTop = 0;
            this.focus = { vi: 0, ci: this.focus.ci };
            this.srvDistinct.clear();
            this.ds.allocate(0); // 'load' → 이 함수 다시 (같은 sig → 빈 목록)
            const s0 = st;
            this.srm.groups(gcols[0], q, [], aggs, this.groupDir.get(gcols[0])).then(rows => {
                if (this.srvG !== s0)
                    return;
                s0.roots = groupNodes(rows, gcols[0], 0, null);
                this.setError(null);
                this.rebuild();
            }).catch(e => { if (this.srvG !== s0)
                return; s0.roots = []; this.setError(String(e.message ?? e), () => { this.srvG = null; this.rebuild(); }); });
        }
        const cur = st;
        const r = flattenServerGroups(st.roots ?? [], path => !this.srvExpandAll !== this.groupToggled.has(path), (col, key) => this.srvGroupLabel(col, key), n => this.loadServerNode(n, cur));
        const members = [];
        for (const v of r.disp)
            if (v >= 0)
                members.push(v);
        for (const g of r.groups)
            this.srvAggOf.set(g.rows, g.node.aggs);
        this.view = Uint32Array.from(members);
        this.disp = r.disp;
        this.groups = r.groups;
        this.empty.hidden = true;
        this.afterFilterChange();
        this.afterView();
    }
    /** @internal */ srvGroupLabel(col, key) {
        if (key == null || key === '')
            return '(없음)';
        const c = this.colInfo(col);
        return c ? this.displayText(c, this.ds.cols[col]?.kind === 'num' ? Number(key) : key, -1) || key : key;
    }
    /** 펼친 서버 그룹: 다음 단계 그룹 또는 멤버 행을 받아 다시 그림 */
    /** @internal */ loadServerNode(n, st) {
        if (n.loading || n.loaded)
            return;
        n.loading = true;
        const next = n.level + 1 < st.gcols.length ? st.gcols[n.level + 1] : null;
        const job = next
            ? this.srm.groups(next, st.q, n.preds, st.aggs, this.groupDir.get(next)).then(rows => { n.children = groupNodes(rows, next, n.level + 1, n); })
            : this.srm.members(st.q, n.preds, n.count, this.opts.serverSource?.groupRowLimit).then(rows => { n.rows = rows; });
        job.catch(e => { if (next)
            n.children = []; this.showTip(String(e.message ?? e)); setTimeout(() => this.hideTip(), 2500); })
            .finally(() => { n.loading = false; n.loaded = true; if (this.srvG === st)
            this.rebuild(); });
        queueMicrotask(() => this.bump()); // 머리행에 "…" 표시
    }
    /** 그룹 순서: groupSort(건수·key·컬럼 집계) → 옛 groupSortBySummary */
    /** @internal */ groupOrder() {
        const g = this.opts.groupSort ?? (this.opts.groupSortBySummary ? { by: { col: this.opts.groupSortBySummary.col, agg: this.opts.groupSortBySummary.agg }, dir: this.opts.groupSortBySummary.dir } : null);
        if (!g || g.by === 'key')
            return undefined;
        if (g.by === 'count')
            return { dir: g.dir, score: rows => rows.length };
        const { col, agg = 'summary' } = g.by, gc = this.colInfo(col);
        const num = (v) => (typeof v === 'number' ? v : v == null || v === '' ? null : Number.isFinite(Number(v)) ? Number(v) : null);
        return { dir: g.dir, score: rows => agg === 'summary' ? (gc ? num(this.summaryValue(gc, rows)) : null) : gc?.def.value ? this.aggComputed(gc, rows, agg) : this.ds.cols[col] ? aggregate(this.ds, rows, col, agg) : null };
    }
    /** 그룹 머리행 정보 (item = getDisplayItems() 의 음수 값). 그룹이 아니면 null */
    groupInfo(item) {
        const g = item < 0 && item > EXTRA ? this.groups[-item - 1] : undefined;
        return g ? { label: g.label, text: this.groupText(g), level: g.level, count: g.count ?? g.rows.length, collapsed: g.collapsed } : null;
    }
    /** 그룹 머리행 접기·펴기 (item = getDisplayItems() 의 음수 값) */
    toggleGroup(item) { const vi = this.disp.indexOf(item); if (vi >= 0)
        this.toggleGroupAt(vi); }
    /** 그룹 단위(날짜·월·년·첫 글자)와 그룹 행 이름 */
    /** @internal */ groupLevel(col) {
        const c = this.colInfo(col);
        const gi = c?.def.groupInterval;
        const disp = (raw) => (raw == null || raw === '' ? '(없음)' : c ? this.displayText(c, raw, -1) || String(raw) : String(raw));
        if (!gi || gi === 'value')
            return { col, labelOf: k => (k == null ? '(없음)' : disp(this.ds.cols[col]?.kind === 'num' ? Number(k) : k)) };
        if (typeof gi === 'function')
            return { col, keyOf: raw => gi(raw), labelOf: k => k ?? '(없음)' };
        const dateStr = (raw) => (raw == null || raw === '' ? null : displayDate(String(raw)));
        if (gi === 'date')
            return { col, keyOf: raw => dateStr(raw)?.slice(0, 10) ?? null, labelOf: k => k ?? '(없음)' };
        if (gi === 'month')
            return { col, keyOf: raw => dateStr(raw)?.slice(0, 7) ?? null, labelOf: k => (k ? `${k.slice(0, 4)}년 ${+k.slice(5, 7)}월` : '(없음)') };
        if (gi === 'year')
            return { col, keyOf: raw => dateStr(raw)?.slice(0, 4) ?? null, labelOf: k => (k ? `${k}년` : '(없음)') };
        // 첫 글자: 한글은 초성으로 묶는다
        return { col, keyOf: raw => { if (raw == null || raw === '')
                return null; const s = disp(raw); const ch = s[0]; const code = ch.charCodeAt(0) - 0xac00; return code >= 0 && code < 11172 ? CHOSUNG[Math.floor(code / 588)] : ch.toUpperCase(); }, labelOf: k => k ?? '(없음)' };
    }
    /** @internal */ treeLabelCol() { return this.opts.tree?.labelCol ?? this.cols.find(c => !c.special)?.id ?? ''; }
    /** 트리 순서(DFS) 표시 목록. 필터 중이면 일치 행의 조상도 함께 보여준다 */
    /** @internal */ buildTree(matches, filtering) {
        const t = this.opts.tree, ds = this.ds;
        const all = ds.allRows();
        const idOf = (r) => { const v = ds.get(r, t.idCol); return v == null ? '' : String(v); };
        const byId = new Map();
        for (const r of all)
            byId.set(idOf(r), r);
        const parentRow = (r) => { const p = ds.get(r, t.parentCol); return p == null || p === '' ? -1 : byId.get(String(p)) ?? -1; };
        let keep = null;
        if (filtering) {
            keep = new Set();
            for (const r of matches) {
                let x = r, guard = 0;
                while (x >= 0 && !keep.has(x) && guard++ < 64) {
                    keep.add(x);
                    x = parentRow(x);
                }
            }
        }
        // 형제 순서: 정렬 키 > orderCol > 입력 순서
        const keys = this.sortKeys.length ? this.sortKeys : t.orderCol ? [{ col: t.orderCol, dir: 'asc' }] : [];
        const ordered = keys.length ? sortView(ds, all, this.withSortText(keys)) : all;
        const kids = new Map(); // 부모 행 → 자식 행들 (-1 = 최상위)
        for (const r of ordered) {
            if (keep && !keep.has(r))
                continue;
            const p = parentRow(r);
            const k = p === r ? -1 : p;
            let a = kids.get(k);
            if (!a)
                kids.set(k, (a = []));
            a.push(r);
        }
        if (!this.treeInit) {
            this.treeInit = true;
            if (t.expandAll === false)
                for (const [p] of kids)
                    if (p >= 0)
                        this.treeCollapsed.add(idOf(p));
        }
        this.treeLevel.clear();
        this.treeKids.clear();
        const order = [];
        const seen = new Set();
        const walk = (p, lv) => {
            for (const r of kids.get(p) ?? []) {
                if (seen.has(r))
                    continue; // 순환 참조 방지
                seen.add(r);
                order.push(r);
                this.treeLevel.set(r, lv);
                if (kids.has(r)) {
                    this.treeKids.add(r);
                    if (filtering || !this.treeCollapsed.has(idOf(r)))
                        walk(r, lv + 1);
                    else
                        markSeen(r);
                }
            }
        };
        const markSeen = (p) => { for (const r of kids.get(p) ?? []) {
            seen.add(r);
            if (kids.has(r)) {
                this.treeKids.add(r);
                markSeen(r);
            }
        } };
        walk(-1, 0);
        // 지연 로딩: 자식을 아직 안 불러온 노드도 ▸ 로 (접힌 상태)
        const lz = t.hasChildren;
        if (lz && t.loadChildren)
            for (const r of order)
                if (!kids.has(r) && !this.treeLoaded.has(idOf(r)) && (typeof lz === 'function' ? lz(r, ds) : ((v) => v === true || v === 'Y' || v === 1 || v === '1')(ds.get(r, lz)))) {
                    this.treeKids.add(r);
                    if (!this.treeLoading.has(idOf(r)))
                        this.treeCollapsed.add(idOf(r));
                }
        this.treeAllKids = kids;
        if (t.checkCascade)
            this.recalcMixed(kids);
        // 부모를 못 찾는 고아 행(순환 등)도 맨 뒤에 보이게
        for (const [p, rs] of kids)
            if (p >= 0 && !seen.has(p))
                for (const r of rs)
                    if (!seen.has(r)) {
                        seen.add(r);
                        order.push(r);
                        this.treeLevel.set(r, 0);
                    }
        const v = Uint32Array.from(order);
        this.view = v;
        this.disp = new Int32Array(v.buffer);
        this.groups = [];
        this.afterView();
    }
    // ─── 페이지 (0부터) ───
    getPage() { return this.page; }
    getPageCount() { return this.pageCount; }
    setPage(n) {
        n = Math.max(0, Math.min(this.pageCount - 1, n | 0));
        if (n === this.page)
            return;
        this.page = n;
        this.focus = { vi: 0, ci: this.focus.ci };
        this.bodyPanes[1].scrollTop = 0;
        this.refreshDisp();
    }
    /** @internal */ renderPager() {
        const ps = Math.max(0, this.opts.pageSize ?? 0) | 0;
        this.pager.hidden = !ps;
        if (!ps)
            return;
        const total = this.pageTotal, p = this.page, pc = this.pageCount;
        const from = total ? p * ps + 1 : 0, to = Math.min(total, (p + 1) * ps);
        const a = Math.max(0, Math.min(p - 4, pc - 10)), b = Math.min(pc, a + 10);
        const btn = (n, t, lbl, dis) => `<button data-p="${n}"${dis ? ' disabled' : ''} aria-label="${lbl}">${t}</button>`;
        let h = btn(0, '«', this.t('첫 페이지'), p === 0) + btn(p - 1, '‹', this.t('이전 페이지'), p === 0);
        for (let i = a; i < b; i++)
            h += `<button data-p="${i}" class="${i === p ? 'og-on' : ''}" aria-label="${i + 1} 페이지"${i === p ? ' aria-current="page"' : ''}>${i + 1}</button>`;
        h += btn(p + 1, '›', this.t('다음 페이지'), p >= pc - 1) + btn(pc - 1, '»', this.t('마지막 페이지'), p >= pc - 1);
        const sizes = [...new Set([20, 50, 100, 500, ps])].sort((x, y) => x - y);
        h += `<select aria-label="${this.t('페이지 크기')}">${sizes.map(z => `<option value="${z}"${z === ps ? ' selected' : ''}>${z}</option>`).join('')}</select>`;
        h += `<span class="og-pinfo">${from.toLocaleString('ko-KR')}–${to.toLocaleString('ko-KR')} / ${total.toLocaleString('ko-KR')}</span>`;
        setHtml(this.pager, h);
    }
    // ─── 마스터-디테일 ───
    isDetailExpanded(row) { return this.detailOpen.has(row); }
    expandDetail(row) { if (!this.opts.detail || this.detailOpen.has(row))
        return; this.detailOpen.add(row); this.refreshDisp(); }
    collapseDetail(row) {
        if (!this.detailOpen.delete(row))
            return;
        this.dropDetailHost(row);
        this.refreshDisp();
    }
    toggleDetail(row) { this.detailOpen.has(row) ? this.collapseDetail(row) : this.expandDetail(row); }
    /** @internal */ dropDetailHost(row) { const h = this.detailHosts.get(row); if (!h)
        return; this.detailHosts.delete(row); h.ro?.disconnect(); try {
        h.cleanup?.();
    }
    catch (e) {
        console.error(e);
    } h.el.remove(); }
    /** @internal */ clearDetails() { for (const r of [...this.detailHosts.keys()])
        this.dropDetailHost(r); this.detailOpen.clear(); this.detailHts.clear(); }
    /** 디테일 펼친 높이 (px) */
    getDetailHeight(row) { return this.detailHts.get(row) ?? Math.max(8, this.opts.detail?.height ?? 0); }
    /** 디테일 높이 직접 지정 (null 이면 detail.height). autoHeight 면 내용이 바뀔 때 다시 맞춤 */
    setDetailHeight(row, px) {
        const old = this.getDetailHeight(row);
        if (px == null)
            this.detailHts.delete(row);
        else
            this.detailHts.set(row, Math.max(8, Math.min(this.opts.detail?.maxHeight ?? 4000, Math.round(px))));
        if (this.getDetailHeight(row) === old || !this.detailOpen.has(row))
            return;
        this.buildOffsets();
        this.sizer.style.height = Math.min(this.totalH(), MAX_SCROLL_PX) + 'px';
        this.pool.forEach(p => (p.key = ''));
        this.requestRender(true);
    }
    /** 디테일 영역 배치: 첫 슬롯 위치에 보이는 폭 전체로, 행보다 위에 */
    /** @internal */ placeDetails(a, b, vTop, vh) {
        const L = this.detailLayer, main = this.bodyPanes[1];
        L.style.width = this.paneWidth[0] + main.clientWidth + this.paneWidth[2] + 'px';
        L.style.height = vh + 'px';
        const shown = new Set();
        let formShown = false;
        for (let vi = a; vi < b; vi++) {
            const it = this.disp[vi];
            if (it > EXTRA)
                continue;
            const x = this.extras[EXTRA - it];
            if (x?.kind === 'form' && this.form?.mode === 'inline') {
                const fe = this.form.el;
                fe.style.top = this.rowTop(vi) - vTop + 'px';
                fe.style.height = this.rowH(vi) + 'px';
                if (fe.hidden)
                    fe.hidden = false;
                formShown = true;
                continue;
            }
            if (!x || x.kind !== 'detail' || shown.has(x.row))
                continue;
            shown.add(x.row);
            let s = vi;
            while (s > 0 && this.disp[s - 1] === it)
                s--;
            let h = this.detailHosts.get(x.row);
            if (!h) {
                const el = document.createElement('div');
                el.className = 'og-dd';
                L.appendChild(el);
                h = { el };
                this.detailHosts.set(x.row, h);
                // autoHeight: 내용 상자(.og-ddi, 높이 auto)를 재서 디테일 높이로 — 내용이 바뀌면(중첩 그리드 로드 등) 다시
                let host = el;
                if (this.opts.detail.autoHeight) {
                    host = document.createElement('div');
                    host.className = 'og-ddi';
                    el.appendChild(host);
                    const row = x.row, fit = () => { if (!el.isConnected)
                        return; const cs = getComputedStyle(el); const pad = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth); this.setDetailHeight(row, Math.ceil(host.offsetHeight + pad)); };
                    if (typeof ResizeObserver === 'function') {
                        h.ro = new ResizeObserver(fit);
                        h.ro.observe(host);
                    }
                    queueMicrotask(fit);
                }
                try {
                    const c = this.opts.detail.render(x.row, host, this);
                    if (typeof c === 'function')
                        h.cleanup = c;
                }
                catch (e) {
                    console.error(e);
                }
            }
            h.el.style.top = this.rowTop(s) - vTop + 'px';
            h.el.style.height = this.rowH(s) + 'px';
            h.el.hidden = false;
        }
        for (const [r, h] of this.detailHosts)
            if (!shown.has(r) && !h.el.hidden)
                h.el.hidden = true;
        if (this.form?.mode === 'inline' && !formShown && !this.form.el.hidden && !this.form.el.contains(document.activeElement))
            this.form.el.hidden = true;
    }
    /** 트리 라벨 칸에서 → 펼침, ← 접기(이미 접혔으면 부모로) */
    /** @internal */ treeKey(right) {
        const t = this.opts.tree, row = this.getFocusedRow();
        if (!t || row < 0 || this.cols[this.focus.ci]?.id !== this.treeLabelCol())
            return false;
        const id = String(this.ds.get(row, t.idCol));
        const kids = this.treeKids.has(row), open = kids && !this.treeCollapsed.has(id);
        if (right) {
            if (kids && !open) {
                this.toggleNode(row, true);
                return true;
            }
            return false;
        }
        if (open) {
            this.toggleNode(row, false);
            return true;
        }
        const pid = this.ds.get(row, t.parentCol);
        if (pid == null)
            return false;
        for (let i = this.focus.vi - 1; i >= 0; i--) {
            const r = this.disp[i];
            if (r >= 0 && String(this.ds.get(r, t.idCol)) === String(pid)) {
                this.setFocus(i, this.focus.ci, false);
                return true;
            }
        }
        return false;
    }
    /** @internal */ toggleGroupAt(vi) {
        const it = this.disp[vi];
        if (it >= 0)
            return;
        const g = this.groups[-it - 1];
        if (!g)
            return;
        const k = g.path ?? g.key ?? '∅';
        this.groupToggled.has(k) ? this.groupToggled.delete(k) : this.groupToggled.add(k);
        this.rebuild();
    }
    // ═══════════════════════ 그룹 패널 ═══════════════════════
    /** @internal */ renderGroupPanel() {
        if (!this.gpanel)
            return;
        const show = !!this.opts.showGroupPanel;
        this.gpanel.hidden = !show;
        if (!show)
            return;
        const g = this.groupCols();
        setHtml(this.gpanel, g.length
            ? g.map((c, i) => `${i ? '<span class="og-gsep">›</span>' : ''}<span class="og-gchip" draggable="true" data-col="${esc(c)}" title="${this.t('끌어서 순서 변경, 패널 밖으로 끌면 해제')}"><span>${esc(this.headerOf(c))}</span><button type="button" data-a="dir" title="${this.t('그룹 정렬 방향')}">${(this.groupDir.get(c) ?? 'asc') === 'asc' ? '▲' : '▼'}</button><button type="button" data-a="del" title="${this.t('그룹 해제')}">✕</button></span>`).join('')
                + `<span class="og-gtools"><button type="button" data-a="expand">${this.t('모두 펼치기')}</button><button type="button" data-a="collapse">${this.t('모두 접기')}</button></span>`
            : '<span>' + this.t('그룹으로 묶을 컬럼의 머리글을 여기로 끌어 놓으세요') + '</span>');
    }
    /** @internal */ onGroupPanelClick(e) {
        const b = e.target.closest('button');
        if (!b)
            return;
        const col = b.closest('.og-gchip')?.dataset.col;
        if (b.dataset.a === 'dir' && col)
            this.setGroupDir(col, (this.groupDir.get(col) ?? 'asc') === 'asc' ? 'desc' : 'asc');
        else if (b.dataset.a === 'del' && col)
            this.removeGroup(col);
        else if (b.dataset.a === 'expand')
            this.expandAllGroups();
        else if (b.dataset.a === 'collapse')
            this.collapseAllGroups();
    }
}
